import { useCallback, useState } from 'react';
import { useSession } from '@web/state/session.tsx';
import { RANGE_PRESETS, isSection, type RangePreset, type Section } from './shared.tsx';

/**
 * What the reports remember between one report and the next, for this tab's
 * session: the range somebody picked, which locations, and the last report
 * opened (the hub's "pick up where you left off" link).
 *
 * One preference shared by every report, so moving from Money received to
 * Crew keeps the dates rather than asking again, and no report needs a
 * second row of controls to carry them. Session storage rather than local:
 * a range is a question asked today, and tomorrow's first report should open
 * on its own default again. Scoped to the person and the company, so a shared
 * computer or a second company never inherits somebody else's dates.
 *
 * `preset` stays null until somebody picks one; until then each report opens
 * on its own default (`defaultRangePreset`).
 */
export interface ReportPreferences {
  preset: RangePreset | null;
  customFrom: string;
  customTo: string;
  scope: 'location' | 'all';
  lastSection: Section | null;
}

const STORAGE_PREFIX = 'blastoclean:reports:v1:';

const EMPTY: ReportPreferences = { preset: null, customFrom: '', customTo: '', scope: 'location', lastSection: null };

/** Kept in memory as well, so a browser that refuses storage still carries the range within the page. */
const inMemory = new Map<string, ReportPreferences>();

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function parse(raw: string | null): ReportPreferences {
  if (!raw) return EMPTY;
  try {
    const value = JSON.parse(raw) as Partial<Record<keyof ReportPreferences, unknown>>;
    return {
      preset: (RANGE_PRESETS as readonly unknown[]).includes(value.preset) ? (value.preset as RangePreset) : null,
      customFrom: typeof value.customFrom === 'string' && ISO_DATE.test(value.customFrom) ? value.customFrom : '',
      customTo: typeof value.customTo === 'string' && ISO_DATE.test(value.customTo) ? value.customTo : '',
      scope: value.scope === 'all' ? 'all' : 'location',
      lastSection: typeof value.lastSection === 'string' && isSection(value.lastSection) ? value.lastSection : null,
    };
  } catch {
    return EMPTY;
  }
}

function read(key: string): ReportPreferences {
  const remembered = inMemory.get(key);
  if (remembered) return remembered;
  try {
    return parse(window.sessionStorage.getItem(key));
  } catch {
    return EMPTY;
  }
}

function write(key: string, value: ReportPreferences): void {
  inMemory.set(key, value);
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Memory keeps the preference for this page's lifetime without storage.
  }
}

export function useReportPreferences(): [ReportPreferences, (change: Partial<ReportPreferences>) => void] {
  const { company, user } = useSession();
  const key = STORAGE_PREFIX + JSON.stringify([user?.id ?? null, company?.id ?? null]);
  const [state, setState] = useState(() => ({ key, value: read(key) }));
  // A different person or company in the same tab reads its own preference.
  const current = state.key === key ? state.value : read(key);
  if (state.key !== key) setState({ key, value: current });

  const update = useCallback(
    (change: Partial<ReportPreferences>) => {
      setState((previous) => {
        const base = previous.key === key ? previous.value : read(key);
        const value = { ...base, ...change };
        write(key, value);
        return { key, value };
      });
    },
    [key],
  );
  return [current, update];
}
